import React, { useEffect, useState } from 'react';
import { UserCircleIcon, CalendarIcon, CheckCircleIcon, TrendingUpIcon, DollarSignIcon, PhoneIcon, MailIcon, MapPinIcon, ShieldIcon, FileTextIcon, MessageSquareIcon, PackageIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Patient, Appointment, ClinicalHistory, FollowUpNote, Opportunity } from '../../types';
import { formatDate, formatTime, formatCurrency, getStatusName, getStageName, getStageColor } from '../../utils/helpers';
import { supabase } from '../../utils/supabase';
export function Patient360() {
  const {
    patients,
    appointments,
    clinicalHistories,
    followUpNotes,
    opportunities,
    getInsurance,
    getDoctor,
    getService,
    getSpecialty
  } = useData();
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [productSales, setProductSales] = useState<any[]>([]);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  // Filter active patients
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-patients-status');
      if (saved) setPatientStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  const activePatients = patients.filter(p => !patientStatuses[p.id]);
  const patientOptions = [{
    value: '',
    label: 'Seleccionar paciente...'
  }, ...activePatients.map(p => ({
    value: p.id,
    label: p.name
  }))];
  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  // Fetch product sales when patient changes
  useEffect(() => {
    if (selectedPatientId) {
      const fetchSales = async () => {
        setIsLoadingSales(true);
        try {
          const {
            data,
            error
          } = await supabase.from('product_sales').select('*').eq('patient_id', selectedPatientId).order('date', {
            ascending: false
          });
          if (!error && data) {
            setProductSales(data);
          }
        } catch (err) {
          console.error('Error fetching product sales:', err);
        } finally {
          setIsLoadingSales(false);
        }
      };
      fetchSales();
    } else {
      setProductSales([]);
    }
  }, [selectedPatientId]);
  // Derived data
  const patientAppointments = appointments.filter(a => a.patientId === selectedPatientId);
  const patientHistories = clinicalHistories.filter(h => h.patientId === selectedPatientId);
  // For follow up notes, we match by contactName (since notes use contactName instead of patientId)
  // This is a best-effort match based on the current data structure
  const patientNotes = followUpNotes.filter(n => n.contactName === selectedPatient?.name);
  // Opportunities use contactId, which might not map 1:1 to patientId, but we'll try to match by name or if contactId matches
  const patientOpportunities = opportunities.filter(o => {
    // If we have a direct link (assuming contactId might sometimes hold patientId in this CRM setup)
    if (o.contactId === selectedPatientId) return true;
    // Otherwise, try to match by name (since contact and patient might be separate entities with same name)
    if (selectedPatient && o.name.toLowerCase().includes(selectedPatient.name.toLowerCase())) return true;
    return false;
  });
  // KPIs
  const totalAppointments = patientAppointments.length;
  const completedAppointments = patientAppointments.filter(a => a.status === 'completada').length;
  const today = new Date().toISOString().split('T')[0];
  const upcomingAppointments = patientAppointments.filter(a => a.status === 'programada' && a.date >= today).length;
  // Calculate total spent (completed appointments + product sales)
  const totalSpentAppointments = patientAppointments.filter(a => a.status === 'completada').reduce((sum, a) => {
    // Handle multiple services
    if (a.serviceIds && a.serviceIds.length > 0) {
      return sum + a.serviceIds.reduce((sSum, sId) => {
        const svc = getService(sId);
        return sSum + (svc?.price || 0);
      }, 0);
    }
    // Single service fallback
    const svc = getService(a.serviceId);
    return sum + (svc?.price || 0);
  }, 0);
  const totalSpentProducts = productSales.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
  const totalSpent = totalSpentAppointments + totalSpentProducts;
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };
  return <div className="space-y-6" data-id="element-3115">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-3116">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-3117" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3118">
          <div data-id="element-3119">
            <div className="flex items-center gap-3 mb-2" data-id="element-3120">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-3121">
                <UserCircleIcon className="w-6 h-6" data-id="element-3122" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-3123">Cliente 360</h1>
            </div>
            <p className="text-teal-100 max-w-xl" data-id="element-3124">
              Vista unificada del paciente
            </p>
          </div>
        </div>
      </div>

      {/* Selector */}
      <Card data-id="element-3125">
        <div className="max-w-md" data-id="element-3126">
          <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={setSelectedPatientId} searchable placeholder="Buscar paciente..." data-id="element-3127" />
        </div>
      </Card>

      {!selectedPatient ? <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-3128">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6" data-id="element-3129">
            <UserCircleIcon className="w-10 h-10 text-gray-400" data-id="element-3130" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-3131">
            Selecciona un paciente
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-3132">
            Busca y selecciona un paciente en el menú desplegable para ver toda
            su información unificada en esta pantalla.
          </p>
        </div> : <div className="space-y-6" data-id="element-3133">
          {/* Patient Profile Card */}
          <Card className="border-t-4 border-t-teal-500 shadow-md overflow-hidden" data-id="element-3134">
            <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-3135">
              <div className="flex-shrink-0" data-id="element-3136">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg" data-id="element-3137">
                  {getInitials(selectedPatient.name)}
                </div>
              </div>

              <div className="flex-1 min-w-0" data-id="element-3138">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-3139">
                  <div data-id="element-3140">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-3141">
                      {selectedPatient.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-3142">
                      <span className="capitalize" data-id="element-3143">
                        {selectedPatient.gender}
                      </span>
                      <span data-id="element-3144">•</span>
                      <span data-id="element-3145">{selectedPatient.age} años</span>
                      {selectedPatient.birthDate && <>
                          <span data-id="element-3146">•</span>
                          <span data-id="element-3147">Nacimiento: {selectedPatient.birthDate}</span>
                        </>}
                      {selectedPatient.cedula && <>
                          <span data-id="element-3148">•</span>
                          <span data-id="element-3149">Cédula: {selectedPatient.cedula}</span>
                        </>}
                    </div>
                  </div>

                  {selectedPatient.insuranceId && <Badge variant="info" className="flex items-center gap-1" data-id="element-3150">
                      <ShieldIcon className="w-3 h-3" data-id="element-3151" />
                      {getInsurance(selectedPatient.insuranceId)?.name || 'Seguro'}
                    </Badge>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-id="element-3152">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3153">
                    <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-3154" />
                    <span data-id="element-3155">{selectedPatient.phone || 'Sin teléfono'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3156">
                    <MailIcon className="w-4 h-4 text-gray-400" data-id="element-3157" />
                    <span className="truncate" data-id="element-3158">
                      {selectedPatient.email || 'Sin email'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3159">
                    <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-3160" />
                    <span className="truncate" data-id="element-3161">
                      {selectedPatient.address || 'Sin dirección'}
                    </span>
                  </div>
                </div>

                {selectedPatient.emergencyContact?.name && <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 inline-block" data-id="element-3162">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3163">
                      Contacto de Emergencia
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3164">
                      {selectedPatient.emergencyContact.name} (
                      {selectedPatient.emergencyContact.relationship})
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3165">
                      {selectedPatient.emergencyContact.phone}
                    </p>
                  </div>}
              </div>
            </div>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3166">
            <Card padding="sm" className="bg-sky-50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-800/50" data-id="element-3167">
              <div className="flex items-center gap-3" data-id="element-3168">
                <div className="p-3 bg-sky-100 dark:bg-sky-800/50 rounded-lg text-sky-600 dark:text-sky-400" data-id="element-3169">
                  <CalendarIcon className="w-6 h-6" data-id="element-3170" />
                </div>
                <div data-id="element-3171">
                  <p className="text-sm font-medium text-sky-600 dark:text-sky-400" data-id="element-3172">
                    Total Citas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3173">
                    {totalAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50" data-id="element-3174">
              <div className="flex items-center gap-3" data-id="element-3175">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg text-emerald-600 dark:text-emerald-400" data-id="element-3176">
                  <CheckCircleIcon className="w-6 h-6" data-id="element-3177" />
                </div>
                <div data-id="element-3178">
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400" data-id="element-3179">
                    Completadas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3180">
                    {completedAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50" data-id="element-3181">
              <div className="flex items-center gap-3" data-id="element-3182">
                <div className="p-3 bg-amber-100 dark:bg-amber-800/50 rounded-lg text-amber-600 dark:text-amber-400" data-id="element-3183">
                  <TrendingUpIcon className="w-6 h-6" data-id="element-3184" />
                </div>
                <div data-id="element-3185">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400" data-id="element-3186">
                    Próximas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3187">
                    {upcomingAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800/50" data-id="element-3188">
              <div className="flex items-center gap-3" data-id="element-3189">
                <div className="p-3 bg-purple-100 dark:bg-purple-800/50 rounded-lg text-purple-600 dark:text-purple-400" data-id="element-3190">
                  <DollarSignIcon className="w-6 h-6" data-id="element-3191" />
                </div>
                <div data-id="element-3192">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-3193">
                    Total Gastado
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3194">
                    {formatCurrency(totalSpent)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs tabs={[{
        id: 'citas',
        label: `Citas (${patientAppointments.length})`,
        icon: <CalendarIcon className="w-4 h-4" data-id="element-3196" />,
        content: <Card padding="none" data-id="element-3197">
                    <div className="overflow-x-auto" data-id="element-3198">
                      <table className="w-full" data-id="element-3199">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3200">
                          <tr data-id="element-3201">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3202">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3203">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3204">
                              Servicio
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3205">
                              Estado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3206">
                          {patientAppointments.length === 0 ? <tr data-id="element-3207">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3208">
                                No hay citas registradas
                              </td>
                            </tr> : patientAppointments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(apt => {
                  const doctor = getDoctor(apt.doctorId);
                  const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
                  const serviceNames = serviceIds.map(id => getService(id)?.name).filter(Boolean).join(', ');
                  return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3209">
                                    <td className="py-3 px-4 text-sm" data-id="element-3210">
                                      <div className="font-medium text-gray-900 dark:text-white" data-id="element-3211">
                                        {formatDate(apt.date)}
                                      </div>
                                      <div className="text-gray-500 text-xs" data-id="element-3212">
                                        {formatTime(apt.time)}
                                      </div>
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3213">
                                      {doctor ? `Dr. ${doctor.name}` : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3214">
                                      {serviceNames || '-'}
                                    </td>
                                    <td className="py-3 px-4" data-id="element-3215">
                                      <Badge variant={apt.status === 'completada' ? 'success' : apt.status === 'cancelada' ? 'danger' : apt.status === 'reprogramada' ? 'warning' : 'info'} data-id="element-3216">
                                        {getStatusName(apt.status)}
                                      </Badge>
                                    </td>
                                  </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'historial',
        label: `Historial Clínico (${patientHistories.length})`,
        icon: <FileTextIcon className="w-4 h-4" data-id="element-3217" />,
        content: <Card padding="none" data-id="element-3218">
                    <div className="overflow-x-auto" data-id="element-3219">
                      <table className="w-full" data-id="element-3220">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3221">
                          <tr data-id="element-3222">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3223">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3224">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3225">
                              Motivo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3226">
                              Diagnóstico
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3227">
                          {patientHistories.length === 0 ? <tr data-id="element-3228">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3229">
                                No hay historiales registrados
                              </td>
                            </tr> : patientHistories.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(h => <tr key={h.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3230">
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3231">
                                    {formatDate(h.date.toISOString().split('T')[0])}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3232">
                                    {h.doctorName || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-3233">
                                    {h.personalBackground?.consultationReason || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-3234">
                                    {h.diagnosis || '-'}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'notas',
        label: `Notas (${patientNotes.length})`,
        icon: <MessageSquareIcon className="w-4 h-4" data-id="element-3235" />,
        content: <div className="space-y-4" data-id="element-3236">
                    {patientNotes.length === 0 ? <Card data-id="element-3237">
                        <div className="py-8 text-center text-gray-500" data-id="element-3238">
                          No hay notas de seguimiento
                        </div>
                      </Card> : patientNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(note => <Card key={note.id} className="bg-gray-50 dark:bg-gray-800/30" data-id="element-3239">
                            <div className="flex justify-between items-start mb-2" data-id="element-3240">
                              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-3241">
                                {note.subject}
                              </h3>
                              <span className="text-xs text-gray-500" data-id="element-3242">
                                {new Date(note.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap" data-id="element-3243">
                              {note.description}
                            </p>
                            <div className="mt-3 text-xs text-gray-500" data-id="element-3244">
                              Por: {note.userName}
                            </div>
                          </Card>)}
                  </div>
      }, {
        id: 'oportunidades',
        label: `Oportunidades (${patientOpportunities.length})`,
        icon: <TrendingUpIcon className="w-4 h-4" data-id="element-3245" />,
        content: <Card padding="none" data-id="element-3246">
                    <div className="overflow-x-auto" data-id="element-3247">
                      <table className="w-full" data-id="element-3248">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3249">
                          <tr data-id="element-3250">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3251">
                              Nombre
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3252">
                              Tipo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3253">
                              Etapa
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3254">
                              Monto
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3255">
                          {patientOpportunities.length === 0 ? <tr data-id="element-3256">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3257">
                                No hay oportunidades registradas
                              </td>
                            </tr> : patientOpportunities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(opp => <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3258">
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3259">
                                    {opp.name}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 capitalize" data-id="element-3260">
                                    {opp.opportunityType.replace(/_/g, ' ')}
                                  </td>
                                  <td className="py-3 px-4" data-id="element-3261">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(opp.stage)}`} data-id="element-3262">
                                      {getStageName(opp.stage)}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-900 dark:text-white" data-id="element-3263">
                                    {formatCurrency(opp.amount)}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'productos',
        label: `Productos (${productSales.length})`,
        icon: <PackageIcon className="w-4 h-4" data-id="element-3264" />,
        content: <Card padding="none" data-id="element-3265">
                    <div className="overflow-x-auto" data-id="element-3266">
                      <table className="w-full" data-id="element-3267">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3268">
                          <tr data-id="element-3269">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3270">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3271">
                              Productos
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3272">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3273">
                          {isLoadingSales ? <tr data-id="element-3274">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-3275">
                                Cargando ventas...
                              </td>
                            </tr> : productSales.length === 0 ? <tr data-id="element-3276">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-3277">
                                No hay compras de productos
                              </td>
                            </tr> : productSales.map(sale => {
                  const items = Array.isArray(sale.items) ? sale.items : [];
                  const itemsDesc = items.map((i: any) => `${i.productName || 'Producto'} (x${i.quantity})`).join(', ');
                  return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3278">
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3279">
                                    {formatDate(sale.date)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-3280">
                                    {itemsDesc || 'Venta de productos'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-emerald-600 dark:text-emerald-400" data-id="element-3281">
                                    {formatCurrency(sale.total)}
                                  </td>
                                </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }]} data-id="element-3195" />
        </div>}
    </div>;
}